/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import moment from 'moment';
import * as React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AccessAlarm,
  Add,
  EditCalendar,
  Info,
  Schedule,
  Task,
} from '@mui/icons-material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LoadingButton } from '@mui/lab';

import { primary } from '../../../../../../../../utils/brandColors';
import theme from '../../../../../../../../utils/theme';
import Horse from '../../../../../../../../components/Horse';
import TaskHistory from '../../../TaskHistory';
import { generateEventsFromSchedules } from '../../../../../../../../utils/scheduleHelper';
import { addSchedule } from '../../../../../../../../redux/schedule/actions';
import { setGlobalModals } from '../../../../../../../../redux/app/actions';
import {
  SCHEDULE_MODAL_TYPE,
  TASK_MODAL_TYPE,
} from '../../../../../../../../redux/app/types';
import { getTagReminder } from '../../../../../../../../redux/tagReminder/actions';
import { TagReminderStatus } from '../..';

const getAlert = (tagReminder) => {
  if (tagReminder.status === 'Incomplete') {
    return (
      <Alert
        severity="error"
        sx={{
          alignItems: 'center',
          mt: 2,
        }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            Active task for this reminder is incomplete
          </Grid>
        </Grid>
      </Alert>
    )
  } if (tagReminder.status === 'Unset') {
    return (
      <Alert
        severity="info"
        sx={{
          alignItems: 'center',
          mt: 2,
        }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            A reminder date cannot be set until there is at least one previous task or one scheduled for the future.
          </Grid>
        </Grid>
      </Alert>
    )
  } if (tagReminder.status === 'Unscheduled') {
    return (
      <Alert
        severity="info"
        sx={{
          alignItems: 'center',
          mt: 2,
        }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            The previous task was completed and the next one is not yet scheduled.
          </Grid>
        </Grid>
      </Alert>
    )
  } if (tagReminder.status === 'PastDue') {
    return (
      <Alert
        severity="error"
        sx={{
          alignItems: 'center',
          mt: 2,
        }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            This horse has past the reminder date without a completed task
          </Grid>
        </Grid>
      </Alert>
    )
  } if (tagReminder.status === 'ScheduleMiss') {
    return (
      <Alert
        severity="warning"
        sx={{
          alignItems: 'center',
          mt: 2,
        }}
        variant="outlined"
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            The next scheduled task is well beyond the reminder date (10+ days)
          </Grid>
        </Grid>
      </Alert>
    )
  }
  return (
    <Alert
      severity="success"
      sx={{
        alignItems: 'center',
        mt: 2,
      }}
      variant="outlined"
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item sx={{ flex: 1 }}>
          This task is scheduled and on track
        </Grid>
      </Grid>
    </Alert>
  )
}

const TagReminderModal = ({
  open,
  onClose,
  tagReminder,
  zIndex,
}) => {
  const dispatch = useDispatch();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = React.useState(false)
  const [addHistory, setAddHistory] = React.useState(false)
  const [error, setError] = React.useState('')
  const [historicalDates, setHistoricalDates] = React.useState([moment().add(-1, 'day').startOf('day').toDate()]);

  React.useEffect(() => {
    setHistoricalDates([moment().add(-1, 'day').startOf('day').toDate()])
    setLoading(false);
    setAddHistory(false)
    setError('');
  }, [])

  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const locationsMap = useSelector((state) => state.location.locationsMap)

  let nextEvents = null;
  let schedule = null;
  if ((tagReminder.nextSchedule && tagReminder.nextSchedule.nextOccurrenceDate)) {
    schedule = tagReminder.nextSchedule

    nextEvents = generateEventsFromSchedules([schedule], [], new Date(), moment(schedule.nextOccurrenceDate).add(1, 'days').toDate(), barnUsersMap, selectedBarn.barnMetadata);
  }

  const reloadTagReminger = () => {
    // regrab reminder to latest info get reset in redux
    setTimeout(() => {
      dispatch(getTagReminder(tagReminder.id)).then(() => {
        setLoading(false);
      });
    }, 750) // Small delay for opensearch to catch up... need better solution
  }

  const handleTaskSelect = (task) => {
    setLoading(true);
    dispatch(setGlobalModals(TASK_MODAL_TYPE, task, null, null, {}, () => {
      reloadTagReminger()
    }))
  }

  // eslint-disable-next-line no-unused-vars
  const handleClickSchedule = (e, event, tr) => {
    const locationTag = event && event.resource && event.resource.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId)
    setLoading(true);
    dispatch(setGlobalModals(SCHEDULE_MODAL_TYPE, null, (event && event.resource) || null, event || null, {
      locationId: (locationTag && locationsMap[locationTag.id])
        ? locationsMap[locationTag.id][0].id
        : null,
      taskTagIds: event
        ? []
        : [{
          id: tr.tag.id,
          tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
        }],
      eventTemplateTagTypeFilter: tr.eventTemplateReminder.eventTemplate.id,
      openInSeries: false,
      startDate: (tr && tr.dueDate && new Date(tr.dueDate).getTime() > new Date().getTime())
        ? new Date(tr.dueDate)
        : null,
    }, () => {
      reloadTagReminger()
    }));
  }

  const handleAddHistoricalRecords = (e) => {
    setLoading(true);
    const promises = [];

    const request = {
      duration: 0,
      name: tagReminder.eventTemplateReminder.eventTemplate.name,
      workflows: [{
        eventTemplateId: tagReminder.eventTemplateReminder.eventTemplate.id,
        tagIds: [tagReminder.tag.id],
        createEvents: true,
        startingAssignment: '',
        startingStatusId: selectedBarn.barnMetadata.statusEndId,
        startingStatusComment: 'Adding historical record for reminder tracking',
        dueDateStatusChangeId: null,
        dueDateTime: null,
        dueDateTimeEndOfDay: false,
        eventDataDefaultValues: tagReminder.eventTemplateReminder.eventTemplate.eventDataDefaultValues || {},
      }],
      overrideCancellations: false, // TODO implement/change this
      type: 'Default',
      completeTasksInPast: true,
      createTasksInPast: true,
    };

    for (let i = 0; i < historicalDates.length; i += 1) {
      promises.push(dispatch(addSchedule(tagReminder.eventTemplateReminder.eventTemplate.id, {
        ...request,
        startDate: historicalDates[i],
        endDate: historicalDates[i],
        rrule: 'FREQ=DAILY;INTERVAL=1',
      })));
    }
    Promise.all(promises).then((results) => {
      const valitResult = !!results.find((result) => (!(result instanceof Error) && result && result.id));
      setError(!valitResult && 'There was an error attempting to add the records Please contact Questri support.');
      setAddHistory(false);
      reloadTagReminger()
      setHistoricalDates([moment().add(-1, 'day').startOf('day').toDate()])
      if (valitResult) {
        onClose(e);
      }
    }).catch(() => {
      setError('There was an error attempting to add the records Please contact Questri support.');
      console.error(error);
      setLoading(false);
    })
  }

  return (
    <Dialog
      // hideBackdrop
      // disablePortal
      id="add-historical-records"
      PaperProps={{
        sx: {
          m: 0,
          minWidth: !fullscreen && (addHistory
            ? 800
            : 500),
        },
      }}
      sx={{ zIndex }}
      open={open}
      fullScreen={fullscreen}
      onClose={onClose}
    >
      <DialogTitle>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item>
            <ListItem dense>
              <ListItemAvatar sx={{ minWidth: 48 }}>
                <Avatar
                  sx={{
                    backgroundColor: (tagReminder.eventTemplateReminder.eventTemplate.eventDataDefaultValues
                      && tagReminder.eventTemplateReminder.eventTemplate.eventDataDefaultValues.calendarDisplayColor)
                      || primary.main,
                  }}
                >
                  <Task />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={tagReminder.eventTemplateReminder.eventTemplate.name}
                primaryTypographyProps={{ variant: 'h5' }}
                secondary={`Due Every (${tagReminder.eventTemplateReminder.reminderDays}) Days`}
              />
            </ListItem>
          </Grid>
          <Grid item>
            {
              barnHorsesMap[`${tagReminder.tag.id}`] && (
                <Horse
                  size={50}
                  horse={barnHorsesMap[`${tagReminder.tag.id}`] && barnHorsesMap[`${tagReminder.tag.id}`][0].horse}
                />
              )
            }
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers>
        <Box
          sx={{ mb: 2 }}
        >{getAlert(tagReminder)}
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AccessAlarm color="secondary" />
              <Box>
                <Typography fontWeight={300}>
                  Next Reminder Date
                </Typography>
                {tagReminder.status !== 'Unset'
                  ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        {tagReminder.dueDate
                          ? moment(tagReminder.dueDate).format('ll')
                          : (
                            <Tooltip title="Questri won't know or calculate a reminder date unless there is at least one previous completed task. Add this horses historical tasks to calculate this now.">
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >Information Required
                              </Box>
                            </Tooltip>
                          )}
                      </Box>
                    </Box>
                  )
                  : (
                    <Tooltip title="Questri won't know or calculate a reminder date unless there is at least one previous completed task. Add this horses historical tasks to calculate this now.">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >No reminder date
                      </Box>
                    </Tooltip>
                  )}
              </Box>
            </Box>
          </Grid>
          <Grid item>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <TagReminderStatus nextEvents={nextEvents} tagReminder={tagReminder} />
              {
                !tagReminder.nextSchedule && (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={(el) => { handleClickSchedule(el, null, tagReminder); }}
                  >
                    Schedule Next Task
                  </Button>
                )
              }

              {
                tagReminder && nextEvents && nextEvents.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography fontWeight={300} align="right">Next Scheduled Task</Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Schedule color="secondary" />
                      <Box>{moment(nextEvents[0].start).format('ll')}</Box>
                      <Button
                        size="small"
                        startIcon={<EditCalendar />}
                        onClick={((el) => { handleClickSchedule(el, nextEvents[0], tagReminder); })}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Box>
                )
              }
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={4}>

          <Grid
            item
            md={addHistory
              ? 6
              : 12}
          >
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                  <Grid item>
                    <Typography variant="h5">
                      Task Records
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Tooltip title="Quickly add past records for this horse by adding the dates of those records">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Info color="secondary" />
                        <Button
                          onClick={() => { setAddHistory(!addHistory) }}
                          size="small"
                          color={addHistory
                            ? 'warning'
                            : 'primary'}
                        >
                          {
                            addHistory
                              ? 'Cancel Add Records'
                              : 'Add Previous Records'
                          }
                        </Button>
                      </Box>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <TaskHistory
                  eventTemplate={tagReminder && tagReminder.eventTemplateReminder && tagReminder.eventTemplateReminder.eventTemplate}
                  tag={tagReminder && tagReminder.tag.id}
                  inline
                  open
                  handleTaskSelect={handleTaskSelect}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={addHistory
              ? 12
              : 0}
            md={addHistory
              ? 6
              : 0}
          >
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Collapse in={addHistory}>
                <Typography variant="h5">
                  Previous Records
                </Typography>

                <Typography
                  gutterBottom
                  sx={{
                    fontWeight: 300,
                    fontSize: 14,
                    lineHeight: '16px',
                    mb: 2,
                  }}
                >
                  Add the dates below to generate historical records for this reminder, task, and horse.
                  Questri will create a task in the past for each date specified. Tasks will be marked complete
                  and tracked as a part of this reminder record.
                </Typography>
                {
                  historicalDates.map((date, index) => (
                    <DatePicker
                      key={`date-${date}-${index}`}
                      label="Date of previous record"
                      value={date}
                      minDate={moment().add(-10, 'year').endOf('day').toDate()}
                      maxDate={moment().add(-1, 'day').startOf('day').toDate()}
                      onChange={(newValue) => {
                        const updatesRecords = historicalDates;
                        updatesRecords[index] = newValue.toDate();
                        setHistoricalDates(JSON.parse(JSON.stringify(updatesRecords)))
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          sx={{ mt: 2 }}
                          fullWidth
                          required
                        />
                      )}
                    />
                  ))
                }
                <Box
                  sx={{
                    textAlign: 'center',
                    mt: 2,
                  }}
                >
                  <Button size="small" startIcon={<Add />} onClick={() => { setHistoricalDates([...historicalDates, moment().add(-1, 'day').startOf('day').toDate()]) }}>
                    Add another record
                  </Button>
                  <Box sx={{ textAlign: 'center' }}><LoadingButton sx={{ mt: 2 }} loading={loading} disabled={loading} variant="contained" onClick={handleAddHistoricalRecords}>Save Records</LoadingButton></Box>
                </Box>

              </Collapse>

            </LocalizationProvider>
          </Grid>
        </Grid>

        {
          error && <Box sx={{ mt: 4 }}><Typography variant="caption" color="error" gutterBottom>{error}</Typography></Box>
        }
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

TagReminderModal.propTypes = {
  tagReminder: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  zIndex: PropTypes.number,
};

TagReminderModal.defaultProps = {
  open: false,
  onClose: () => { },
  zIndex: 1100,
};

export default TagReminderModal;
